(() => {
  const ROOT_SELECTOR = '[data-swalk-shell][data-swalk-destination]';

  const DRAFT_FIELDS = {
    'send-to': { name: 'properties[_SWALK Send To]', value: '' }
  };

  class SwalkDestination {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-swalk-personaliser-dialog]');
      this.messageStep = root.querySelector('[data-swalk-shell-step="message"]');
      this.destinationStep = root.querySelector('[data-swalk-shell-step="destination"]');
      this.blankConflictStep = root.querySelector('[data-swalk-shell-step="blank-conflict"]');
      this.blankResolvedStep = root.querySelector('[data-swalk-shell-step="blank-resolved"]');
      this.choiceInputs = Array.from(root.querySelectorAll('[data-swalk-destination-choice]'));
      this.closeButtons = Array.from(root.querySelectorAll('[data-swalk-destination-close]'));
      this.backButton = root.querySelector('[data-swalk-destination-back]');
      this.continueButton = root.querySelector('[data-swalk-destination-continue]');
      this.error = root.querySelector('[data-swalk-destination-error]');
      this.title = root.querySelector('[data-swalk-destination-title]');
      this.intro = root.querySelector('[data-swalk-destination-intro]');
      this.normalBody = root.querySelector('[data-swalk-destination-normal]');
      this.normalFooter = root.querySelector('[data-swalk-destination-footer]');
      this.conflictTitle = root.querySelector('[data-swalk-conflict-title]');
      this.conflictCopy = root.querySelector('[data-swalk-destination-conflict-copy]');
      this.conflictSendButton = root.querySelector('[data-swalk-conflict-send-with-order]');
      this.conflictCheckoutButton = root.querySelector('[data-swalk-conflict-checkout]');
      this.conflictBackButton = root.querySelector('[data-swalk-conflict-back]');
      this.conflictError = root.querySelector('[data-swalk-conflict-error]');
      this.conflictCloseButtons = Array.from(root.querySelectorAll('[data-swalk-conflict-close]'));
      this.blankResolvedTitle = root.querySelector('[data-swalk-blank-resolved-title]');
      this.blankResolvedAddButton = root.querySelector('[data-swalk-blank-resolved-add]');
      this.blankResolvedBackButton = root.querySelector('[data-swalk-blank-resolved-back]');
      this.blankResolvedError = root.querySelector('[data-swalk-blank-resolved-error]');
      this.blankResolvedCloseButtons = Array.from(root.querySelectorAll('[data-swalk-blank-resolved-close]'));
      this.recovery = root.querySelector('[data-swalk-destination-recovery]');
      this.recoveryCopy = root.querySelector('[data-swalk-destination-recovery-copy]');
      this.checkoutButton = root.querySelector('[data-swalk-destination-checkout]');
      this.productId = String(root.dataset.productId || '');
      this.sourceForm = null;
      this.personalisationDetail = null;
      this.choice = '';
      this.lastTrigger = null;
      this.adding = false;
      this.blankAdding = false;
      this.conflictDetail = null;
      this.cartReturnSession = null;
      this.openSequence = 0;

      this.bind();
    }

    bind() {
      document.addEventListener('swalk:personalisation:continue', (event) => {
        const detail = event.detail || {};
        if (String(detail.productId || '') !== this.productId) return;

        this.open(detail);
      });

      document.addEventListener('swalk:cart:destination-conflict', (event) => {
        const detail = event.detail || {};
        if (!detail.isBlank || String(detail.productId || '') !== this.productId) return;
        this.openConflict(detail);
      });

      this.choiceInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.setChoice(input.value);
          this.hideError();
        });
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.conflictCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.blankResolvedCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.backButton?.addEventListener('click', () => this.goBack());
      this.continueButton?.addEventListener('click', () => this.handleContinue());
      this.checkoutButton?.addEventListener('click', () => this.openBasket('destination'));
      this.conflictSendButton?.addEventListener('click', () => this.handleBlankWithOrder());
      this.conflictCheckoutButton?.addEventListener('click', () => this.openBasket('blank-conflict'));
      this.conflictBackButton?.addEventListener('click', () => this.leaveConflict());
      this.blankResolvedAddButton?.addEventListener('click', () => this.handleBlankAdd('Me', this.blankResolvedAddButton, this.blankResolvedError));
      this.blankResolvedBackButton?.addEventListener('click', () => this.close());

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.destinationStep?.hidden) this.close();
      });
      this.root.addEventListener('swalk:shell:closed', () => {
        this.openSequence += 1;
        this.setBasketLoading(false);
      });
    }

    resolveForm(variantId, returnFocus, sourceForm) {
      if (sourceForm?.isConnected) return sourceForm;
      const triggerForm = returnFocus?.closest?.('.product__buy-buttons')?.querySelector('form[data-type="add-to-cart-form"]');
      if (triggerForm) return triggerForm;
      const forms = Array.from(document.querySelectorAll('.product__buy-buttons form[data-type="add-to-cart-form"]'));
      return forms.find((form) => String(form.querySelector('[name="id"]')?.value || '') === String(variantId || '')) || forms[0] || null;
    }

    get personaliseTrigger() {
      return this.sourceForm?.closest('.product__buy-buttons')?.querySelector('[data-swalk-personalise-trigger]') ||
        document.querySelector('[data-swalk-personalise-trigger]');
    }

    ensureDraftFields() {
      if (!this.sourceForm) return;

      Object.entries(DRAFT_FIELDS).forEach(([key, config]) => {
        let field = this.sourceForm.querySelector(`[data-swalk-destination-property="${key}"]`);
        if (field) return;

        field = document.createElement('input');
        field.type = 'hidden';
        field.name = config.name;
        field.value = config.value;
        field.disabled = true;
        field.dataset.swalkDestinationProperty = key;
        this.sourceForm.appendChild(field);
      });
    }

    getDraftField(key) {
      return this.sourceForm?.querySelector(`[data-swalk-destination-property="${key}"]`) || null;
    }

    async open(detail) {
      const sequence = ++this.openSequence;
      this.personalisationDetail = detail || {};
      this.sourceForm = this.resolveForm(detail?.variantId, detail?.returnFocus, detail?.sourceForm);
      this.lastTrigger = detail?.returnFocus || this.personaliseTrigger;
      this.ensureDraftFields();
      this.syncFromDraft();
      this.showDestinationState();
      this.hideError();
      this.setBasketLoading(true);

      let basketState = {};
      try {
        basketState = await window.SwalkCart?.getBasketState?.() || {};
      } catch (error) {
        basketState = {};
      }
      if (sequence !== this.openSequence) return;
      this.revealDestination(basketState);
    }

    revealDestination(basketState) {
      this.applyBasketState(basketState || {});
      this.setBasketLoading(false);
      this.showShellStep('destination');
      this.showShell(this.dialog);
    }

    close({ restoreFocus = true, preserveJourney = false } = {}) {
      if (this.root.hidden) return;
      this.openSequence += 1;
      this.setBasketLoading(false);
      this.root.hidden = true;
      document.body.classList.remove('swalk-personaliser-open');
      if (!preserveJourney) document.body.classList.remove('swalk-journey-open');
      this.showDestinationState();

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(restoreFocus ? this.lastTrigger : null);
      } else if (restoreFocus) {
        this.lastTrigger?.focus?.();
      }
    }

    goBack() {
      this.openSequence += 1;
      this.setBasketLoading(false);
      this.showShellStep('message');
      const messageField = this.root.querySelector('[data-swalk-message]');
      if (typeof window.trapFocus === 'function' && this.dialog) {
        window.trapFocus(this.dialog, messageField || this.dialog);
      } else {
        messageField?.focus?.();
      }
    }

    showShellStep(step) {
      const destinationActive = step === 'destination';
      const blankConflictActive = step === 'blank-conflict';
      const blankResolvedActive = step === 'blank-resolved';
      if (this.messageStep) this.messageStep.hidden = destinationActive || blankConflictActive || blankResolvedActive;
      if (this.destinationStep) this.destinationStep.hidden = !destinationActive;
      if (this.blankConflictStep) this.blankConflictStep.hidden = !blankConflictActive;
      if (this.blankResolvedStep) this.blankResolvedStep.hidden = !blankResolvedActive;
      this.root.dataset.swalkShellStepActive = blankResolvedActive
        ? 'blank-resolved'
        : blankConflictActive
          ? 'blank-conflict'
          : destinationActive
            ? 'destination'
            : 'message';
      const labelledBy = blankResolvedActive
        ? this.blankResolvedTitle?.id
        : blankConflictActive
          ? this.conflictTitle?.id
          : destinationActive
            ? this.title?.id
            : this.root.querySelector('[data-swalk-shell-step="message"] h2')?.id;
      this.dialog?.setAttribute('aria-labelledby', labelledBy || '');
    }

    setBasketLoading(active) {
      this.root.setAttribute('aria-busy', String(active));
      if (active && this.recovery) this.recovery.hidden = true;
      this.choiceInputs.forEach((input) => {
        if (active) input.disabled = true;
      });
      if (this.continueButton) {
        this.continueButton.disabled = active;
        this.continueButton.setAttribute('aria-disabled', String(active));
      }
    }

    setChoice(value) {
      const nextChoice = value === 'Them' ? 'Them' : value === 'Me' ? 'Me' : '';
      const nextInput = this.choiceInputs.find((input) => input.value === nextChoice);
      if (nextInput?.disabled) return false;
      this.choice = nextChoice;
      this.choiceInputs.forEach((input) => {
        input.checked = input.value === this.choice;
      });
      return true;
    }

    syncFromDraft() {
      const sendTo = this.getDraftField('send-to')?.value || '';
      this.setChoice(sendTo);
    }

    validate() {
      this.hideError();

      if (!this.choice) {
        this.showError('Choose where the card is going.');
        this.choiceInputs[0]?.focus();
        return false;
      }

      return true;
    }

    saveDraft() {
      this.ensureDraftFields();

      const setValue = (key, value) => {
        const field = this.getDraftField(key);
        if (field) field.value = value == null ? '' : String(value);
      };

      setValue('send-to', this.choice);
    }

    setAdding(active) {
      this.adding = active;
      if (!this.continueButton) return;
      this.continueButton.setAttribute('aria-busy', String(active));
      if (!active) {
        this.continueButton.disabled = false;
        this.continueButton.removeAttribute('aria-disabled');
      }
      this.continueButton.classList.toggle('loading', active);
      this.continueButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !active);
    }

    showDestinationState() {
      if (this.title) this.title.textContent = 'Where’s it going?';
      if (this.intro) {
        this.intro.textContent = 'Choose where this card should actually end up.';
        this.intro.hidden = false;
      }
      if (this.normalBody) this.normalBody.hidden = false;
      if (this.normalFooter) this.normalFooter.hidden = false;
    }

    applyBasketState({ destination: basketDestination = '', cardCount = 0 } = {}) {
      this.choiceInputs.forEach((input) => {
        input.disabled = false;
      });
      if (this.recovery) this.recovery.hidden = true;

      if (basketDestination !== 'Me' && basketDestination !== 'Them') return;

      const unavailableChoice = basketDestination === 'Me' ? 'Them' : 'Me';
      const unavailableInput = this.choiceInputs.find((input) => input.value === unavailableChoice);
      if (unavailableInput) unavailableInput.disabled = true;
      if (this.recoveryCopy) {
        const singular = cardCount === 1;
        if (basketDestination === 'Me') {
          this.recoveryCopy.textContent = `You already have ${singular ? 'a card' : 'cards'} in your basket being sent to you. To send another card to a different address, complete this order first or remove ${singular ? 'the card' : 'the cards'} from your basket.`;
        } else {
          this.recoveryCopy.textContent = `You already have ${singular ? 'a card' : 'cards'} in your basket being sent to someone else. To have this card sent to you instead, complete this order first or remove ${singular ? 'the card' : 'the cards'} from your basket.`;
        }
      }
      if (this.recovery) this.recovery.hidden = false;
      this.setChoice(basketDestination);
      this.saveDraft();
    }

    openConflict(detail) {
      this.conflictDetail = detail;
      this.setBasketLoading(false);
      if (detail.sourceForm?.isConnected) this.sourceForm = detail.sourceForm;
      if (detail.isBlank) this.lastTrigger = detail.trigger || this.lastTrigger;
      if (this.conflictCopy) {
        const singular = detail.basketCount === 1;
        this.conflictCopy.textContent = `You already have ${singular ? 'a card' : 'cards'} in your basket being sent to someone else. You can send this blank card to the same address or complete your current order first.`;
      }
      this.hideConflictError();
      this.setBlankAdding(this.conflictSendButton, false);
      this.showShellStep('blank-conflict');
      this.showShell(this.conflictSendButton);
    }

    openBlankResolved(detail) {
      this.conflictDetail = detail;
      this.setBasketLoading(false);
      if (detail?.sourceForm?.isConnected) this.sourceForm = detail.sourceForm;
      if (detail?.trigger) this.lastTrigger = detail.trigger;
      this.hideBlankError(this.blankResolvedError);
      this.setBlankAdding(this.blankResolvedAddButton, false);
      this.showShellStep('blank-resolved');
      this.showShell(this.blankResolvedAddButton);
    }

    showShell(focusTarget) {
      this.root.hidden = false;
      document.body.classList.add('swalk-personaliser-open');
      document.body.classList.add('swalk-journey-open');

      requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function' && this.dialog) {
          window.trapFocus(this.dialog, focusTarget || this.dialog);
        } else {
          (focusTarget || this.dialog)?.focus?.();
        }
      });
    }

    openBasket(originStep) {
      const cartDrawer = document.querySelector('cart-drawer');
      const context = originStep === 'destination'
        ? { personalisation: this.personalisationDetail }
        : { blankConflict: this.conflictDetail };
      this.close({ restoreFocus: false, preserveJourney: true });
      if (cartDrawer?.open) {
        this.startCartReturnSession(cartDrawer, originStep, context);
        cartDrawer.open(this.lastTrigger);
      } else {
        document.body.classList.remove('swalk-journey-open');
        window.location.assign(window.routes.cart_url);
      }
    }

    startCartReturnSession(cartDrawer, originStep, context) {
      this.endCartReturnSession();
      const session = {
        cartDrawer,
        originRoot: this.root,
        originStep,
        productId: this.productId,
        context,
        autoReturnWhenResolved: true,
        conflictResolved: false,
        drawerOpened: cartDrawer.hasAttribute('open') || cartDrawer.classList.contains('modal--open'),
        basketCheck: 0,
        returning: false,
        observer: null,
        unsubscribe: null
      };
      this.cartReturnSession = session;
      document.body.classList.add('swalk-cart-return-open');

      session.observer = new MutationObserver(() => {
        const isOpen = cartDrawer.hasAttribute('open') || cartDrawer.classList.contains('modal--open');
        if (isOpen) {
          session.drawerOpened = true;
          return;
        }
        if (!session.drawerOpened) return;
        this.returnFromCartSession(session, { conflictResolved: session.conflictResolved });
      });
      session.observer.observe(cartDrawer, { attributes: true, attributeFilter: ['open', 'class'] });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        session.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.checkCartReturnConflict(session));
      }
    }

    async checkCartReturnConflict(session) {
      if (this.cartReturnSession !== session || session.returning) return;
      const basketCheck = ++session.basketCheck;
      try {
        const basketState = await window.SwalkCart?.getBasketState?.();
        if (this.cartReturnSession !== session || session.returning || basketCheck !== session.basketCheck) return;
        const conflictResolved = session.originStep === 'blank-conflict'
          ? basketState?.destination !== 'Them'
          : !basketState?.destination;
        if (session.autoReturnWhenResolved && conflictResolved) {
          session.conflictResolved = true;
          session.cartDrawer.close?.();
        }
      } catch (error) {
        // Keep the drawer open if the basket cannot be checked; a manual close still returns safely.
      }
    }

    async returnFromCartSession(session, { conflictResolved = false } = {}) {
      if (this.cartReturnSession !== session || session.returning) return;
      session.returning = true;
      session.observer?.disconnect();
      document.body.classList.remove('swalk-cart-return-open');

      let basketState = null;
      try {
        basketState = await window.SwalkCart?.getBasketState?.() || {};
      } catch (error) {
        basketState = null;
      }
      if (this.cartReturnSession !== session) return;

      const originStep = session.originStep;
      const context = session.context;
      this.endCartReturnSession(session);

      if (originStep === 'destination') {
        this.personalisationDetail = context.personalisation || this.personalisationDetail;
        this.sourceForm = this.resolveForm(
          this.personalisationDetail?.variantId,
          this.personalisationDetail?.returnFocus,
          this.personalisationDetail?.sourceForm
        );
        this.ensureDraftFields();
        this.syncFromDraft();
        this.showDestinationState();
        this.hideError();
        this.revealDestination(basketState || {});
        return;
      }

      const blankConflictResolved = basketState
        ? basketState.destination !== 'Them'
        : conflictResolved;
      if (blankConflictResolved) {
        this.openBlankResolved(context.blankConflict || this.conflictDetail);
        return;
      }

      const detail = context.blankConflict || this.conflictDetail;
      if (!detail) {
        document.body.classList.remove('swalk-journey-open');
        this.lastTrigger?.focus?.();
        return;
      }
      if (basketState) detail.basketCount = basketState.cardCount;
      this.openConflict(detail);
    }

    endCartReturnSession(session = this.cartReturnSession, { endJourney = false } = {}) {
      if (!session || (this.cartReturnSession && this.cartReturnSession !== session)) return;
      session.observer?.disconnect();
      session.unsubscribe?.();
      if (this.cartReturnSession === session) this.cartReturnSession = null;
      document.body.classList.remove('swalk-cart-return-open');
      if (endJourney) document.body.classList.remove('swalk-journey-open');
    }

    leaveConflict() {
      this.close();
    }

    setBlankAdding(button, active) {
      if (!button) return;
      this.blankAdding = active;
      if (!active) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
      button.setAttribute('aria-busy', String(active));
      button.classList.toggle('loading', active);
      button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !active);
    }

    async handleBlankWithOrder() {
      this.hideConflictError();
      this.handleBlankAdd('Them', this.conflictSendButton, this.conflictError);
    }

    async handleBlankAdd(destination, button, errorElement) {
      if (this.blankAdding) return;
      this.hideBlankError(errorElement);
      this.setBlankAdding(button, true);
      let submitted = false;
      let resetSubmission = () => {};

      try {
        const sourceForm = this.conflictDetail?.sourceForm?.isConnected ? this.conflictDetail.sourceForm : this.sourceForm;
        const productForm = sourceForm?.closest('product-form');
        if (!sourceForm || !productForm) throw new Error('Could not add this card. Please try again.');

        const blankPreparation = window.SwalkCart?.prepareBlankProperties?.(sourceForm, destination);
        if (!blankPreparation?.valid) {
          throw new Error(blankPreparation?.message || 'This design is missing its print artwork. Please choose another design or try again later.');
        }
        const originalSubmitButton = productForm.submitButton;
        const onSuccess = () => {
          resetSubmission();
          this.setBlankAdding(button, false);
          this.close({ restoreFocus: false });
        };
        const onError = (event) => {
          resetSubmission();
          this.setBlankAdding(button, false);
          this.showBlankError(errorElement, event.detail?.message || 'Could not add this card. Please try again.');
        };
        resetSubmission = () => {
          productForm.removeEventListener('swalk:cart:add-success', onSuccess);
          productForm.removeEventListener('swalk:cart:add-error', onError);
          productForm.submitButton = originalSubmitButton;
        };
        productForm.addEventListener('swalk:cart:add-success', onSuccess, { once: true });
        productForm.addEventListener('swalk:cart:add-error', onError, { once: true });
        productForm.submitButton = button;
        sourceForm.requestSubmit();
        submitted = true;
      } catch (error) {
        resetSubmission();
        this.showBlankError(errorElement, error.message || 'Could not add this card. Please try again.');
      } finally {
        if (!submitted) this.setBlankAdding(button, false);
      }
    }

    async handleContinue() {
      if (this.adding || !this.validate()) return;
      this.setAdding(true);
      let submitted = false;
      let resetSubmission = () => {};

      try {
        const productForm = this.sourceForm?.closest('product-form');
        if (!this.sourceForm || !productForm) throw new Error('Could not add this card. Please try again.');

        this.saveDraft();
        this.sourceForm.querySelectorAll('[data-swalk-blank-property]').forEach((field) => {
          field.disabled = true;
        });
        this.sourceForm.querySelectorAll('[data-swalk-personalisation-property], [data-swalk-destination-property]').forEach((field) => {
          field.disabled = false;
        });

        const originalSubmitButton = productForm.submitButton;
        resetSubmission = () => {
          productForm.removeEventListener('swalk:cart:add-success', onSuccess);
          productForm.removeEventListener('swalk:cart:add-error', onError);
          productForm.submitButton = originalSubmitButton;
        };
        const onSuccess = () => {
          resetSubmission();
          this.setAdding(false);
          this.close({ restoreFocus: false });
        };
        const onError = (event) => {
          resetSubmission();
          this.setAdding(false);
          if (event.detail?.conflict) {
            this.showDestinationState();
            this.applyBasketState({
              destination: event.detail.conflict.existingDestination,
              cardCount: event.detail.conflict.basketCount
            });
          } else {
            this.showError(event.detail?.message || 'Could not add this card. Please try again.');
          }
        };
        productForm.addEventListener('swalk:cart:add-success', onSuccess, { once: true });
        productForm.addEventListener('swalk:cart:add-error', onError, { once: true });
        productForm.submitButton = this.continueButton;
        this.sourceForm.requestSubmit();
        submitted = true;
      } catch (error) {
        resetSubmission();
        this.showError(error.message || 'Could not add this card. Please try again.');
      } finally {
        if (!submitted) this.setAdding(false);
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    hideConflictError() {
      this.hideBlankError(this.conflictError);
    }

    showBlankError(element, message) {
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
    }

    hideBlankError(element) {
      if (!element) return;
      element.textContent = '';
      element.hidden = true;
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.swalkDestinationReady === 'true') return;
      root.dataset.swalkDestinationReady = 'true';
      new SwalkDestination(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('swalk:destination:init', init);
})();
